/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { Coupon, Customer } from "App/Models";
import { Customers } from "App/IOC/Services";
import { Filter, HandleListError, Identifiable, Named } from "@Internal/Contracts";
import { CustomError, PagerSorter, SelectionFilter, TextFilter } from "@Internal/Models";
import Coupons from "../Coupons";

let defaultFilters: Filter[] = [];

async function getByCoupon( keyword?: string ): Promise<[ Coupon[], CustomError | undefined ]> {
    const nameFilter = new TextFilter( "Name", "" );
    nameFilter.Value = [ keyword !== undefined ? keyword : "" ];

    const [ coupons, , error ] = await Coupons.Find( [ nameFilter ], new PagerSorter() );

    if( error !== undefined )
        return [ [], error ];

    return [ coupons, undefined ];
}

/* SUGGESTIONS AU FIL DE LA FRAPPE ( Fred, 26/09/2026 ) : la societe et le nom
   du contact se choisissent dans une liste, comme sur « Abonnements ». Les
   clients qui « contiennent » le texte tape, une seule fois chaque valeur. La
   valeur choisie part au service comme avant : le back la cherche en
   « contient » ( ILIKE ), le filtre garde donc son sens. */
async function distinct(
    field: "Company" | "Name",
    keyword?: string
): HandleListError<Named & Identifiable> {
    const filter = new TextFilter( field, "" );
    filter.Value = [ keyword !== undefined ? keyword : "" ];

    const [ customers, , error ] = await Customers.Find( [ filter ], new PagerSorter( 25 ) );

    const values = [ ...new Set(
        customers
            .map( ( customer: Customer ): string => String( customer[ field ] || "" ).trim() )
            .filter( ( value: string ): boolean => value !== "" )
    ) ].sort( ( a: string, b: string ): number => a.localeCompare( b, "fr" ) );

    const options = values.map(
        ( value: string ): Named & Identifiable => ( { Id: value, Name: value } )
    );

    return [ options, error ];
}

function getByCompany( keyword?: string ): HandleListError<Named & Identifiable> {
    return distinct( "Company", keyword );
}

function getByName( keyword?: string ): HandleListError<Named & Identifiable> {
    return distinct( "Name", keyword );
}

function Get(): Filter[] {
    if( defaultFilters.length === 0 )
        defaultFilters = [
            /* UNE SEULE SAISIE ( Fred, 26/09/2026 ) : societe, contact, e-mails,
               telephone ou adresse — le back cherche dans tout ( filtre `Search` ).
               C'est lui qui retrouve un client dont la societe est mal saisie. */
            new TextFilter(
                "Search",
                $t( "CUSTOMERS_filterSearch" ),
                $t( "CUSTOMERS_filterSearchPlaceholder" )
            ),

            SelectionFilter.Request(
                "Coupon",
                $t( "COUPONS_filter" ),
                getByCoupon,
                $t( "COUPONS_filterPlaceholder" )
            ),

            new TextFilter(
                "Email",
                $t( "CUSTOMERS_filterEmail" ),
                $t( "CUSTOMERS_filterEmailPlaceholder" )
            ),

            SelectionFilter.Request(
                "Company",
                $t( "CUSTOMERS_filterCompany" ),
                getByCompany,
                $t( "CUSTOMERS_filterCompanyPlaceholder" )
            ),

            SelectionFilter.Request(
                "Name",
                $t( "GLOBAL_filterCustomerName" ),
                getByName,
                $t( "GLOBAL_filterCustomerPlaceholder" )
            ),

            new TextFilter(
                "Phone",
                $t( "CUSTOMERS_filterPhone" ),
                $t( "CUSTOMERS_filterPhonePlaceholder" )
            ),

            new TextFilter(
                "Address",
                $t( "CUSTOMERS_filterAddress" ),
                $t( "CUSTOMERS_filterAddressPlaceholder" )
            )
        ];

    return defaultFilters;
}

export default {
    Get
};
